<template>
  <div class="AccordionBlock layout-container" :class="desktopWidth('sm')">
    <div
      v-if="blok.title || blok.buttonText"
      class="flex flex-row flex-wrap justify-between items-center gap-8 mb-16"
      :class="{'flex-col text-center': blok.textAlign === 'center'}"
    >
      <div class="type-headline-lg desk:type-headline-2xl">{{ blok.title }}</div>
      <NuxtLink :to="$sbLinkFix(blok.buttonLink)" :target="$sbLinkTarget(blok.buttonLink)">
        <div
          v-if="blok.buttonText"
          class="btn btn--primary"
          :class="{
            'btn--secondary': blok.buttonStyle === 'secondary',
            'btn--ghost': blok.buttonStyle === 'ghost',
            'btn--text': blok.buttonStyle === 'plain',
          }"
        >
          {{ blok.buttonText }}
        </div>
      </NuxtLink>
    </div>
    <div
      v-for="item in blok.itemList"
      :key="item._uid"
      class="py-16 desk:py-20 border-b border-light"
    >
      <div
        class="select-none cursor-pointer flex justify-between items-center type-headline-sm desk:type-headline-base"
        @click="toggleItem(item._uid)"
      >
        {{ item.label }}
        <img
          :src="openAccordionItems.includes(item._uid) ? '/icons/angle-up.svg' : '/icons/angle-down.svg'"
          class="w-20 h-20 desk:w-24 desk:h-24"
        >
      </div>
      <transition
        name="accordion"
        @enter="enter"
        @leave="leave"
      >
        <div
          v-show="openAccordionItems.includes(item._uid)"
          class="overflow-hidden"
        >
          <div class="type-sm desk:type-base pt-8 wysiwyg">
            <ExtendedWysiwyg :content="item.content" />
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { AccordionBlokType, StoryBlokLink, StoryBlokPropBase } from '~/constants/types/storyblok';
import useDesktopWidth from '~/composeables/useDesktopWidth';

type AccordionBlokType = {
  _uid: string;
  itemList: Array<{ _uid: string; label: string; content: any }>;
  title: string;
  textAlign: 'left' | 'center';
  buttonText: string;
  buttonStyle: 'primary' | 'secondary' | 'ghost' | 'plain';
  buttonLink: StoryBlokLink;
};

type Props = StoryBlokPropBase & {
  blok: AccordionBlokType;
};

const props = defineProps<Props>();

const openAccordionItems = ref<string[]>([]);

const { desktopWidth } = useDesktopWidth(props);

const toggleItem = (id: string) => {
  if (openAccordionItems.value.includes(id)) {
    openAccordionItems.value = openAccordionItems.value.filter(item => item !== id);
  } else {
    openAccordionItems.value.push(id);
  }
};

const enter = (el) => {
  el.style.maxHeight = el.scrollHeight + 'px';
};

const leave = (el) => {
  el.style.maxHeight = el.scrollHeight + 'px';
  setTimeout(() => {
    el.style.maxHeight = 0;
  });
};
</script>

<style scoped>
.accordion-enter-active, .accordion-leave-active {
  transition: max-height 0.3s ease;
}
.accordion-enter-from, .accordion-leave-to {
  max-height: 0;
}
</style>
